import { useCallback, useRef } from 'react';

import { useMessage } from 'components/ui';
import {
  type AtlasGqlUpdateMyPreferencesInput,
  useUpdateUserPreferencesMutation,
} from 'types/atlas-graphql';

import { runPreferenceMutation } from './runPreferenceMutation';

type UpdateUserPreferenceCallOptions = {
  successDuration?: number;
  onSuccess?: () => void;
  resetGuardOnSuccess?: boolean;
  silent?: boolean;
};

export const useUpdateUserPreference = ({
  successMessage,
  errorMessage,
  mutationOptions,
}: {
  successMessage: string;
  errorMessage: string;
  mutationOptions?: Parameters<typeof useUpdateUserPreferencesMutation>[0];
}) => {
  const message = useMessage();
  const isUpdatingRef = useRef(false);
  const [updateUserPreferences, { loading }] = useUpdateUserPreferencesMutation(mutationOptions);

  const update = useCallback(
    (input: AtlasGqlUpdateMyPreferencesInput, options?: UpdateUserPreferenceCallOptions) =>
      runPreferenceMutation({
        isUpdatingRef,
        mutate: () => updateUserPreferences({ variables: { input } }),
        getPayload: (result) => result.data?.updateMyPreferences,
        message,
        successMessage,
        errorMessage,
        successDuration: options?.successDuration,
        onSuccess: options?.onSuccess,
        resetGuardOnSuccess: options?.resetGuardOnSuccess,
        silent: options?.silent,
      }),
    [errorMessage, message, successMessage, updateUserPreferences]
  );

  return { loading, update };
};
